import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { Annotation, FieldValue } from '../../components/ai-viewer/eventing/types'

export interface Transaction {
  date: string
  description: string
  amount: number
  type: 'credit' | 'debit'
}

export interface ExtractedData {
  account_number: string
  statement_period: string
  beginning_balance: number
  ending_balance: number
  total_deposits: number
  total_withdrawals: number
  transaction_count: number
}

export interface ValidationResult {
  isValid: boolean
  balanceCheck: {
    beginningBalance: number
    endingBalance: number
    expectedEnding: number
    difference: number
    totalCredits: number
    totalDebits: number
    transactionCount: number
  }
  issues: Array<{
    field: string
    severity: 'error' | 'warning' | 'info'
    message: string
  }>
}

interface DocumentsState {
  extractedData: ExtractedData | null
  transactions: Transaction[]
  validationResult: ValidationResult | null
  loading: boolean
  error: string | null
  // Viewer-related state
  annotations: Annotation[]
  fields: FieldValue[]
}

const initialState: DocumentsState = {
  extractedData: {
    account_number: '****4567',
    statement_period: 'Feb 1 - Feb 28, 2025',
    beginning_balance: 38856.91,
    ending_balance: 43412.47,
    total_deposits: 8543.21,
    total_withdrawals: 3987.65,
    transaction_count: 47
  },
  transactions: [
    { date: '02/01', description: 'Direct Deposit - Payroll', amount: 2500.00, type: 'credit' },
    { date: '02/03', description: 'Transfer from Savings', amount: 1000.00, type: 'credit' },
    { date: '02/05', description: 'Mortgage Payment', amount: 1200.00, type: 'debit' },
    { date: '02/07', description: 'Grocery Store', amount: 145.32, type: 'debit' },
    { date: '02/10', description: 'Online Transfer', amount: 500.00, type: 'credit' }
  ],
  validationResult: {
    isValid: false,
    balanceCheck: {
      beginningBalance: 38856.91,
      endingBalance: 43412.47,
      expectedEnding: 43412.45,
      difference: 0.02,
      totalCredits: 8543.21,
      totalDebits: 3987.65,
      transactionCount: 47
    },
    issues: [
      {
        field: 'balance',
        severity: 'warning',
        message: 'Small discrepancy of $0.02 detected'
      },
      {
        field: 'transactions',
        severity: 'info',
        message: 'Possible duplicate transaction on Feb 15th'
      }
    ]
  },
  loading: false,
  error: null,
  annotations: [],
  fields: [],
}

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setExtractedData(state, action: PayloadAction<ExtractedData>) {
      state.extractedData = action.payload
    },
    
    setTransactions(state, action: PayloadAction<Transaction[]>) {
      state.transactions = action.payload
    },
    
    updateTransaction(state, action: PayloadAction<{ index: number; transaction: Transaction }>) {
      const { index, transaction } = action.payload
      if (state.transactions[index]) {
        state.transactions[index] = transaction
      }
    },
    
    setValidationResult(state, action: PayloadAction<ValidationResult>) {
      state.validationResult = action.payload
    },
    
    updateExtractedField(state, action: PayloadAction<{ key: keyof ExtractedData; value: any }>) {
      if (state.extractedData) {
        (state.extractedData as any)[action.payload.key] = action.payload.value
      }
    },
    
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
    
    clearDocumentData(state) {
      state.extractedData = null
      state.transactions = []
      state.validationResult = null
      state.error = null
      state.annotations = []
      state.fields = []
    },

    // Viewer annotations/fields
    setAnnotations(state, action: PayloadAction<Annotation[]>) {
      state.annotations = action.payload
    },
    upsertAnnotation(state, action: PayloadAction<Annotation>) {
      const idx = state.annotations.findIndex(a => a.id === action.payload.id)
      if (idx === -1) state.annotations.push(action.payload)
      else state.annotations[idx] = action.payload
    },
    setFields(state, action: PayloadAction<FieldValue[]>) {
      state.fields = action.payload
    }
  }
})

export const {
  setExtractedData,
  setTransactions,
  updateTransaction,
  setValidationResult,
  updateExtractedField,
  setLoading,
  setError,
  clearDocumentData,
  setAnnotations,
  upsertAnnotation,
  setFields
} = documentsSlice.actions

export default documentsSlice.reducer
